// src/App.js
import React from "react";
import { BrowserRouter, Routes, Route, Navigate, Link } from "react-router-dom";
import { AuthProvider, useAuth } from "./AuthContext";
import { LanguageProvider } from './contexts/LanguageContext';
import Login from "./Login";
import Register from "./Register";
import Dashboard from "./Dashboard";
import AdminDashboard from "./AdminDashboard";
import ValidatorDashboard from "./ValidatorDashboard";
import CoordinatorDashboard from "./CoordinatorDashboard";
import Reports from "./Reports";
import TspUpload from "./TspUpload";
import AuthSanityTest from "./AuthSanityTest";
import PartnerDashboard from "./PartnerDashboard";
import FarmerProfile from "./FarmerProfile";
import BiocharToSoil from './BiocharToSoil';

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#0f2419',
        color: 'white' 
      }}>
        Loading...
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" replace />;
}

// Smart redirect based on user role
function RoleBasedRedirect() {
  const { user } = useAuth();
  
  if (!user) return <Navigate to="/login" replace />;
  
  switch (user.role) {
    case 'Admin':
      return <Navigate to="/admin" replace />;
    case 'Validator':
      return <Navigate to="/validator" replace />;
    case 'Partner':
      return <Navigate to="/partner" replace />;
    case 'Coordinator':
      return <Navigate to="/coordinator" replace />;
    default:
      return <Navigate to="/dashboard" replace />;
  }
}

export default function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <BrowserRouter>
          <Routes>
            {/* public */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/auth-test" element={<AuthSanityTest />} />

            {/* protected - Farmer */}
            <Route
              path="/dashboard"
              element={
                <RequireAuth>
                  <Dashboard />
                </RequireAuth>
              }
            />
            <Route
              path="/profile"
              element={
                <RequireAuth>
                  <FarmerProfile />
                </RequireAuth>
              }
            />
            <Route
              path="/biochar-to-soil"
              element={
                <RequireAuth>
                  <BiocharToSoil />
                </RequireAuth>
              }
            />
            
            {/* protected - Admin */}
            <Route
              path="/admin"
              element={
                <RequireAuth>
                  <AdminDashboard />
                </RequireAuth>
              }
            />
            
            {/* protected - Validator */}
            <Route
              path="/validator"
              element={
                <RequireAuth>
                  <ValidatorDashboard />
                </RequireAuth>
              }
            />
            
            {/* protected - Partner */}
            <Route
              path="/partner"
              element={
                <RequireAuth>
                  <PartnerDashboard />
                </RequireAuth>
              }
            />
            
            {/* protected - Coordinator */}
            <Route
              path="/coordinator"
              element={
                <RequireAuth>
                  <CoordinatorDashboard />
                </RequireAuth>
              }
            />
            
            <Route
              path="/reports"
              element={
                <RequireAuth>
                  <Reports />
                </RequireAuth>
              }
            />
            <Route
              path="/tsp-upload"
              element={
                <RequireAuth>
                  <TspUpload />
                </RequireAuth>
              }
            />

            {/* Smart redirect based on role */}
            <Route path="/" element={<RoleBasedRedirect />} />
            
            {/* 404 */}
            <Route
              path="*"
              element={
                <div style={{ padding: 24, background: '#0f2419', minHeight: '100vh', color: 'white' }}>
                  Page not found. <Link to="/login" style={{ color: '#4ade80' }}>Go to login</Link>
                </div>
              }
            />
          </Routes>
        </BrowserRouter>
      </LanguageProvider>
    </AuthProvider>
  );
}